import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Badge,
  Divider,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SpeedIcon from '@mui/icons-material/Speed';
import StraightenIcon from '@mui/icons-material/Straighten';
import CropFreeIcon from '@mui/icons-material/CropFree';
import TimelineIcon from '@mui/icons-material/Timeline';
import { 
  ECHO_MEASUREMENTS, 
  MEASUREMENT_CATEGORIES, 
  MODALITIES,
  validateMeasurement,
  calculateDerivedMeasurement,
  getMeasurementsByCategory
} from '../utils/echoMeasurements';
import {
  PixelSpacingManager,
  MeasurementProcessor,
  MeasurementValidator,
  MeasurementStorage,
  MeasurementKeyboardManager,
  MEASUREMENT_TOOL_TYPES
} from '../utils/measurementTools';
import './MeasurementPanel.css';

const EnhancedMeasurementPanel = ({ 
  onSelectMeasurement, 
  onMeasurementComplete,
  activeMeasurementTool,
  patientGender = 'male',
  patientAge = null,
  currentImageId = null,
  isInMeasurementMode = false,
  onClearAllMeasurements,
  onToolActivation
}) => {
  const [selectedTab, setSelectedTab] = useState('structure');
  const [measurements, setMeasurements] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [patientInputs, setPatientInputs] = useState({
    HR: 70,
    BSA: 1.8,
    RAP: 5
  });
  const [pixelSpacingInfo, setPixelSpacingInfo] = useState(null);
  const [measurementWarnings, setMeasurementWarnings] = useState({});
  const [showPixelSpacingDialog, setShowPixelSpacingDialog] = useState(false);
  const [velocityScale, setVelocityScale] = useState(1.0);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [measurementCount, setMeasurementCount] = useState({});
  const [activeTool, setActiveTool] = useState(null);
  
  const keyboardManagerRef = useRef(null);

  // Initialize keyboard shortcuts
  useEffect(() => {
    const callbacks = {
      activateLengthTool: () => handleQuickToolActivation('length'),
      activateAreaTool: () => handleQuickToolActivation('area'),
      activateWindowLevelTool: () => onToolActivation?.('WindowLevel'),
      cancelCurrentMeasurement: () => {
        setSelectedMeasurement(null);
        setActiveTool(null);
        onToolActivation?.('WindowLevel');
      },
      clearAllMeasurements: () => handleClearAllMeasurements()
    };

    keyboardManagerRef.current = new MeasurementKeyboardManager(callbacks);
    keyboardManagerRef.current.enable();

    return () => {
      if (keyboardManagerRef.current) {
        keyboardManagerRef.current.disable();
      }
    };
  }, [onToolActivation]);

  // Quick tool activation
  const handleQuickToolActivation = useCallback((toolType) => {
    // Find a measurement that uses this tool type
    const allMeasurements = Object.values(ECHO_MEASUREMENTS)
      .flatMap(group => group.measurements)
      .filter(m => m.tool === toolType);
    
    if (allMeasurements.length > 0) {
      const measurement = allMeasurements[0];
      handleSelectMeasurement(measurement);
    }
  }, []);

  // Check pixel spacing when image changes
  useEffect(() => {
    if (currentImageId) {
      const spacing = PixelSpacingManager.extractPixelSpacing(currentImageId);
      setPixelSpacingInfo(spacing);
      
      if (!spacing) {
        setMeasurementWarnings(prev => ({
          ...prev,
          pixelSpacing: 'No pixel spacing data found - measurements will be in pixels only'
        }));
      } else {
        setMeasurementWarnings(prev => {
          const updated = { ...prev };
          delete updated.pixelSpacing;
          return updated;
        });
      }
    }
  }, [currentImageId]);

  // Load saved measurements
  useEffect(() => {
    if (currentImageId) {
      const saved = MeasurementStorage.getMeasurements(currentImageId);
      setMeasurements(saved);
      updateMeasurementCount(saved);
    }
  }, [currentImageId]);

  // Update measurement count by category
  const updateMeasurementCount = useCallback((measurementData) => {
    const counts = {};
    Object.entries(MEASUREMENT_CATEGORIES).forEach(([key, category]) => {
      const categoryMeasurements = getMeasurementsByCategory(category);
      const measuredCount = categoryMeasurements.filter(m => measurementData[m.id]).length;
      counts[key] = {
        measured: measuredCount,
        total: categoryMeasurements.length
      };
    });
    setMeasurementCount(counts);
  }, []);

  // Handle measurement selection
  const handleSelectMeasurement = useCallback((measurement) => {
    setSelectedMeasurement(measurement);
    setActiveTool(measurement.tool);
    setShowInstructions(true);
    
    // Notify parent component to activate the appropriate tool
    if (onSelectMeasurement) {
      onSelectMeasurement(measurement);
    }
  }, [onSelectMeasurement]);

  // Handle measurement completion with enhanced processing
  const handleMeasurementComplete = useCallback((annotation, measurementType) => {
    if (!currentImageId || !annotation || !measurementType) return;

    let result = null;

    // Process measurement based on type
    switch (measurementType.tool) {
      case MEASUREMENT_TOOL_TYPES.LENGTH:
        result = MeasurementProcessor.processLengthMeasurement(annotation, currentImageId);
        break;
      case MEASUREMENT_TOOL_TYPES.AREA:
      case MEASUREMENT_TOOL_TYPES.ELLIPSE:
        result = MeasurementProcessor.processAreaMeasurement(annotation, currentImageId);
        break;
      case MEASUREMENT_TOOL_TYPES.ANGLE:
        result = MeasurementProcessor.processAngleMeasurement(annotation, currentImageId);
        break;
      case MEASUREMENT_TOOL_TYPES.VELOCITY:
        result = MeasurementProcessor.processVelocityMeasurement(annotation, currentImageId, velocityScale);
        break;
      case MEASUREMENT_TOOL_TYPES.VTI:
        result = MeasurementProcessor.processVTIMeasurement(annotation, currentImageId, velocityScale);
        break;
      default:
        console.warn('Unknown measurement tool type:', measurementType.tool);
        return;
    }

    if (!result) {
      console.error('Failed to process measurement');
      return;
    }

    // Add validation
    const validation = MeasurementValidator.validateMeasurement(
      measurementType.id,
      result.value,
      result.unit,
      { gender: patientGender, age: patientAge }
    );

    const enhancedResult = {
      ...result,
      validation: validation,
      measurementType: measurementType,
      measured: true
    };

    // Update state
    setMeasurements(prev => {
      const updated = {
        ...prev,
        [measurementType.id]: enhancedResult
      };
      updateMeasurementCount(updated);
      return updated;
    });

    // Save to storage
    MeasurementStorage.saveMeasurement(currentImageId, measurementType.id, enhancedResult);

    // Calculate derived measurements
    updateCalculatedMeasurements();
    
    // Notify parent component
    if (onMeasurementComplete) {
      onMeasurementComplete(measurementType.id, result.value, result.unit);
    }

    // Show warning if no pixel spacing
    if (!result.hasRealWorldUnits) {
      setMeasurementWarnings(prev => ({
        ...prev,
        [measurementType.id]: result.warning
      }));
    }
  }, [currentImageId, patientGender, patientAge, velocityScale, onMeasurementComplete]);

  // Update calculated measurements
  const updateCalculatedMeasurements = useCallback(() => {
    Object.values(ECHO_MEASUREMENTS).forEach(group => {
      group.measurements
        .filter(m => m.calculated)
        .forEach(calcMeasurement => {
          const values = {};
          let canCalculate = true;
          
          // Extract measurement IDs from formula
          const formula = calcMeasurement.formula;
          const measurementIds = formula.match(/[A-Z_]+/g) || [];
          
          measurementIds.forEach(id => {
            if (measurements[id.toLowerCase()]) {
              values[id] = measurements[id.toLowerCase()].value;
            } else if (patientInputs[id]) {
              values[id] = patientInputs[id];
            } else {
              canCalculate = false;
            }
          });
          
          if (canCalculate) {
            const result = calculateDerivedMeasurement(calcMeasurement.id, values);
            if (result !== null) {
              const validation = validateMeasurement(calcMeasurement.id, result, patientGender, patientAge);
              const calculatedResult = {
                value: result.toFixed(1),
                unit: calcMeasurement.unit,
                timestamp: new Date().toISOString(),
                validation,
                calculated: true,
                hasRealWorldUnits: true
              };
              
              setMeasurements(prev => ({
                ...prev,
                [calcMeasurement.id]: calculatedResult
              }));

              // Save calculated measurement
              if (currentImageId) {
                MeasurementStorage.saveMeasurement(currentImageId, calcMeasurement.id, calculatedResult);
              }
            }
          }
        });
    });
  }, [measurements, patientInputs, patientGender, patientAge, currentImageId]);

  // Clear all measurements
  const handleClearAllMeasurements = useCallback(() => {
    if (currentImageId) {
      MeasurementStorage.clearMeasurements(currentImageId);
      setMeasurements({});
      setMeasurementWarnings({});
      updateMeasurementCount({});
      
      if (onClearAllMeasurements) {
        onClearAllMeasurements();
      }
    }
  }, [currentImageId, onClearAllMeasurements]);

  // Delete individual measurement
  const handleDeleteMeasurement = useCallback((measurementId) => {
    setMeasurements(prev => {
      const updated = { ...prev };
      delete updated[measurementId];
      updateMeasurementCount(updated);
      return updated;
    });

    setMeasurementWarnings(prev => {
      const updated = { ...prev };
      delete updated[measurementId];
      return updated;
    });

    if (currentImageId) {
      const stored = MeasurementStorage.getAllMeasurements();
      if (stored[currentImageId]) {
        delete stored[currentImageId][measurementId];
        localStorage.setItem(MeasurementStorage.STORAGE_KEY, JSON.stringify(stored));
      }
    }
  }, [currentImageId]);

  // Render measurement status icon
  const renderStatusIcon = (measurementId) => {
    const measurement = measurements[measurementId];
    if (!measurement) return null;
    
    if (measurement.validation?.status === 'normal') {
      return <CheckCircleIcon color="success" fontSize="small" />;
    } else if (measurement.validation?.status === 'above' || measurement.validation?.status === 'below') {
      return <WarningIcon color="warning" fontSize="small" />;
    } else if (!measurement.hasRealWorldUnits) {
      return <ErrorIcon color="error" fontSize="small" title="Pixel measurements only" />;
    }
    return null;
  };

  // Render tool icon
  const renderToolIcon = (tool) => {
    switch (tool) {
      case MEASUREMENT_TOOL_TYPES.LENGTH:
        return <StraightenIcon fontSize="small" />;
      case MEASUREMENT_TOOL_TYPES.AREA:
      case MEASUREMENT_TOOL_TYPES.ELLIPSE:
        return <CropFreeIcon fontSize="small" />;
      case MEASUREMENT_TOOL_TYPES.VELOCITY:
        return <SpeedIcon fontSize="small" />;
      case MEASUREMENT_TOOL_TYPES.VTI:
        return <TimelineIcon fontSize="small" />;
      default:
        return <CalculateIcon fontSize="small" />;
    }
  };

  // Format normal range for display
  const formatNormalRange = (range) => {
    if (typeof range === 'string') return range;
    if (Array.isArray(range)) {
      return `${range[0]} - ${range[1]}`;
    }
    if (typeof range === 'object') {
      if (range.min !== undefined && range.max !== undefined) {
        return `${range.min} - ${range.max}`;
      } else if (range.min !== undefined) {
        return `> ${range.min}`;
      } else if (range.max !== undefined) {
        return `< ${range.max}`;
      }
    }
    return '';
  };

  // Render structure-based view
  const renderStructureView = () => {
    return Object.entries(MEASUREMENT_CATEGORIES).map(([key, category]) => {
      const categoryMeasurements = getMeasurementsByCategory(category);
      const count = measurementCount[key] || { measured: 0, total: categoryMeasurements.length };
      
      return (
        <Accordion 
          key={key} 
          expanded={expandedCategories[key] || false}
          onChange={(e, isExpanded) => 
            setExpandedCategories(prev => ({ ...prev, [key]: isExpanded }))
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
              <Typography variant="h6">{category}</Typography>
              <Badge 
                badgeContent={count.measured}
                max={count.total}
                color={count.measured === count.total ? "success" : "primary"}
              >
                <Chip 
                  label={`${count.measured}/${count.total}`}
                  size="small"
                  color={count.measured === count.total ? "success" : "default"}
                />
              </Badge>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Measurement</TableCell>
                    <TableCell>Tool</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Normal Range</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryMeasurements.map(measurement => {
                    const measurementData = measurements[measurement.id];
                    const hasWarning = measurementWarnings[measurement.id];
                    
                    return (
                      <TableRow key={measurement.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {renderStatusIcon(measurement.id)}
                            <Typography variant="body2">
                              {measurement.name}
                            </Typography>
                            {hasWarning && (
                              <Tooltip title={hasWarning}>
                                <WarningIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {renderToolIcon(measurement.tool)}
                            <Chip 
                              label={measurement.view} 
                              size="small" 
                              className="view-chip"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {editingMeasurement === measurement.id ? (
                            <TextField
                              size="small"
                              defaultValue={measurementData?.value || ''}
                              onBlur={(e) => {
                                const value = parseFloat(e.target.value);
                                if (!isNaN(value)) {
                                  const result = {
                                    value,
                                    unit: measurement.unit,
                                    timestamp: new Date().toISOString(),
                                    hasRealWorldUnits: true,
                                    manualEntry: true
                                  };
                                  
                                  const validation = MeasurementValidator.validateMeasurement(
                                    measurement.id,
                                    value,
                                    measurement.unit,
                                    { gender: patientGender, age: patientAge }
                                  );
                                  
                                  result.validation = validation;
                                  
                                  setMeasurements(prev => ({
                                    ...prev,
                                    [measurement.id]: result
                                  }));
                                  
                                  if (currentImageId) {
                                    MeasurementStorage.saveMeasurement(currentImageId, measurement.id, result);
                                  }
                                }
                                setEditingMeasurement(null);
                              }}
                              autoFocus
                              sx={{ width: 80 }}
                            />
                          ) : (
                            <Typography 
                              variant="body2"
                              color={measurementData?.validation?.status === 'normal' ? 'text.primary' : 
                                     measurementData?.validation?.status === 'above' || measurementData?.validation?.status === 'below' ? 'warning.main' : 'text.primary'}
                              fontWeight={measurementData?.validation?.status !== 'normal' ? 'bold' : 'normal'}
                            >
                              {measurementData 
                                ? `${measurementData.value} ${measurementData.unit}`
                                : '-'
                              }
                              {measurementData?.calculated && (
                                <Chip 
                                  label="calc" 
                                  size="small" 
                                  className="calculated-value"
                                  sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                                />
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {measurement.normalRange && formatNormalRange(
                              measurement.normalRange[patientGender] || 
                              measurement.normalRange.all || 
                              'Age dependent'
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Measure">
                              <IconButton 
                                size="small"
                                onClick={() => handleSelectMeasurement(measurement)}
                                color={selectedMeasurement?.id === measurement.id ? "primary" : "default"}
                              >
                                <CalculateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Manual entry">
                              <IconButton 
                                size="small"
                                onClick={() => setEditingMeasurement(measurement.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {measurementData && (
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDeleteMeasurement(measurement.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // Check if any measurements exist
  const hasMeasurements = Object.keys(measurements).length > 0;

  return (
    <Box className="measurement-panel">
      {/* Header with pixel spacing info */}
      <Box className="measurement-header" p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Echo Measurements</Typography>
          {hasMeasurements && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAllMeasurements}
              color="error"
            >
              Clear All
            </Button>
          )}
        </Box>

        {/* Pixel spacing status */}
        {pixelSpacingInfo ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Pixel Spacing: {pixelSpacingInfo.rowSpacing.toFixed(3)} × {pixelSpacingInfo.columnSpacing.toFixed(3)} mm/pixel
              <br />
              Source: {pixelSpacingInfo.source}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No pixel spacing data found - measurements will be in pixels only
              <Button 
                size="small" 
                onClick={() => setShowPixelSpacingDialog(true)}
                sx={{ ml: 1 }}
              >
                Learn More
              </Button>
            </Typography>
          </Alert>
        )}

        {/* Measurement mode indicator */}
        {isInMeasurementMode && selectedMeasurement && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Active:</strong> {selectedMeasurement.name}
              <br />
              <em>{selectedMeasurement.instructions}</em>
            </Typography>
          </Alert>
        )}

        {/* Patient inputs for calculations */}
        <Box display="flex" gap={2} mt={2}>
          <TextField
            label="HR (bpm)"
            size="small"
            type="number"
            value={patientInputs.HR}
            onChange={(e) => {
              setPatientInputs(prev => ({ ...prev, HR: parseFloat(e.target.value) }));
              updateCalculatedMeasurements();
            }}
            sx={{ width: 100 }}
          />
          <TextField
            label="BSA (m²)"
            size="small"
            type="number"
            value={patientInputs.BSA}
            onChange={(e) => {
              setPatientInputs(prev => ({ ...prev, BSA: parseFloat(e.target.value) }));
              updateCalculatedMeasurements();
            }}
            sx={{ width: 100 }}
          />
          <TextField
            label="RAP (mmHg)"
            size="small"
            type="number"
            value={patientInputs.RAP}
            onChange={(e) => {
              setPatientInputs(prev => ({ ...prev, RAP: parseFloat(e.target.value) }));
              updateCalculatedMeasurements();
            }}
            sx={{ width: 100 }}
          />
        </Box>

        {/* Advanced settings toggle */}
        <Box mt={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showAdvancedSettings}
                onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                size="small"
              />
            }
            label="Advanced Settings"
          />
        </Box>

        {/* Advanced settings */}
        {showAdvancedSettings && (
          <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" gutterBottom>
              Doppler Settings
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Velocity Scale:
              </Typography>
              <Slider
                value={velocityScale}
                onChange={(e, value) => setVelocityScale(value)}
                min={0.1}
                max={5.0}
                step={0.1}
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: 40 }}>
                {velocityScale.toFixed(1)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Keyboard shortcuts info */}
      <Box px={2} pb={2}>
        <Typography variant="caption" color="text.secondary">
          Shortcuts: L - Length | A - Area | W - Window/Level | ESC - Cancel | Shift+Del - Clear All
        </Typography>
      </Box>

      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
        <Tab label="By Structure" value="structure" />
        <Tab label="By Modality" value="modality" />
        <Tab label="Report Summary" value="report" />
      </Tabs>

      <Box className="measurement-content" p={2}>
        {selectedTab === 'structure' && renderStructureView()}
        {selectedTab === 'modality' && (
          <Typography>Modality view - Coming soon</Typography>
        )}
        {selectedTab === 'report' && (
          <Typography>Report summary - Coming soon</Typography>
        )}
      </Box>

      {/* Instructions Dialog */}
      <Dialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {renderToolIcon(selectedMeasurement?.tool)}
            <Typography variant="h6">{selectedMeasurement?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info" icon={false}>
              <Typography variant="body2" fontWeight="bold">View:</Typography>
              <Typography variant="body2">{selectedMeasurement?.view}</Typography>
            </Alert>
          </Box>
          
          <Typography variant="body2" paragraph>
            {selectedMeasurement?.instructions}
          </Typography>
          
          {selectedMeasurement?.normalRange && (
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold">Normal Range:</Typography>
              <Typography variant="body2">
                {formatNormalRange(
                  selectedMeasurement.normalRange[patientGender] || 
                  selectedMeasurement.normalRange.all || 
                  'Age dependent'
                )}
                {' '}{selectedMeasurement.unit}
              </Typography>
            </Box>
          )}

          {!pixelSpacingInfo && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                No pixel spacing data available - measurement will be in pixels only
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstructions(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowInstructions(false);
              // Tool activation is handled by parent component
            }}
          >
            Start Measuring
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pixel Spacing Info Dialog */}
      <Dialog
        open={showPixelSpacingDialog}
        onClose={() => setShowPixelSpacingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Pixel Spacing Information</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Pixel spacing defines the physical size of each pixel in the image, allowing accurate real-world measurements.
          </Typography>
          
          <Typography variant="h6" gutterBottom>DICOM Tags Used:</Typography>
          <ul>
            <li><strong>(0028,0030) Pixel Spacing:</strong> Primary source for pixel dimensions</li>
            <li><strong>(0018,1164) Imager Pixel Spacing:</strong> Alternative source from imaging equipment</li>
            <li><strong>Image metadata:</strong> Fallback from image processing pipeline</li>
          </ul>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Impact on Measurements:</Typography>
          <ul>
            <li><strong>With pixel spacing:</strong> Measurements in real-world units (mm, cm)</li>
            <li><strong>Without pixel spacing:</strong> Measurements in pixels only (less clinically useful)</li>
          </ul>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              For clinical use, ensure your DICOM files contain proper pixel spacing metadata.
              Contact your imaging equipment vendor if this data is missing.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPixelSpacingDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedMeasurementPanel;
